import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { generateSlug } from "@/lib/blog";
import { Pencil, Trash2, Check, X, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { useConfirm } from "./ConfirmDialog";

export type Category = { id: string; name: string; slug: string; description: string | null; sort_order: number };

export const loadCategories = async (): Promise<Category[]> => {
  const { data } = await supabase.from("blog_categories" as never).select("*").order("sort_order").order("name");
  return (data as unknown as Category[]) ?? [];
};

type Counts = Record<string, { total: number; live: number }>;

const CategoriesManager = () => {
  const { toast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [items, setItems] = useState<Category[]>([]);
  const [counts, setCounts] = useState<Counts>({});
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const refresh = async () => {
    setItems(await loadCategories());
    const { data } = await supabase.from("posts" as never).select("category,status,published_at");
    const c: Counts = {};
    const now = Date.now();
    ((data as unknown as { category: string | null; status: string; published_at: string | null }[]) ?? []).forEach((p) => {
      if (!p.category) return;
      const e = (c[p.category] ??= { total: 0, live: 0 });
      e.total++;
      if (p.status === "published" && (!p.published_at || new Date(p.published_at).getTime() <= now)) e.live++;
    });
    setCounts(c);
  };
  useEffect(() => { refresh(); }, []);

  const err = (m: string) => toast({ title: "Erro", description: m.includes("duplicate") ? "Já existe uma categoria com esse nome." : m, variant: "destructive" });

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const max = items.reduce((m, i) => Math.max(m, i.sort_order), 0);
    const { error } = await supabase.from("blog_categories" as never).insert({ name: name.trim(), slug: generateSlug(name), description: desc.trim() || null, sort_order: max + 1 } as never);
    if (error) return err(error.message);
    setName(""); setDesc(""); refresh();
  };

  const saveEdit = async (c: Category) => {
    const { error } = await supabase.from("blog_categories" as never).update({ name: editName.trim(), slug: generateSlug(editName), description: editDesc.trim() || null } as never).eq("id", c.id);
    if (error) return err(error.message);
    if (editName.trim() !== c.name) await supabase.from("posts" as never).update({ category: editName.trim() } as never).eq("category", c.name);
    setEditing(null); refresh();
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    const results = await Promise.all(
      next.map((c, i) => supabase.from("blog_categories" as never).update({ sort_order: i + 1 } as never).eq("id", c.id)),
    );
    const failed = results.find((r) => r.error);
    if (failed?.error) err(failed.error.message);
    refresh();
  };

  const remove = async (c: Category) => {
    const n = counts[c.name]?.total ?? 0;
    const ok = await confirm({
      title: `Excluir a categoria "${c.name}"?`,
      description: n ? `${n} artigo${n > 1 ? "s ficarão" : " ficará"} sem categoria. Os artigos não serão excluídos.` : "Nenhum artigo usa esta categoria.",
      confirmLabel: "Excluir",
      destructive: true,
    });
    if (!ok) return;
    const { error } = await supabase.from("blog_categories" as never).delete().eq("id", c.id);
    if (error) return err(error.message);
    if (n) await supabase.from("posts" as never).update({ category: null } as never).eq("category", c.name);
    toast({ title: "Categoria excluída" });
    refresh();
  };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="flex flex-col md:flex-row gap-2 p-4 rounded-xl border border-border bg-card">
        <Input placeholder="Nome da categoria" value={name} onChange={(e) => setName(e.target.value)} className="md:w-64" />
        <Input placeholder="Descrição (opcional)" value={desc} onChange={(e) => setDesc(e.target.value)} />
        <Button type="submit" className="gap-1.5"><Plus size={16} /> Adicionar</Button>
      </form>

      <p className="text-xs text-muted-foreground">A ordem abaixo é a mesma dos filtros do blog público.</p>

      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {items.length === 0 && <p className="p-6 text-sm text-muted-foreground">Nenhuma categoria ainda.</p>}
        {items.map((c, idx) => {
          const n = counts[c.name] ?? { total: 0, live: 0 };
          return (
            <div key={c.id} className="flex items-center gap-3 p-4">
              {editing === c.id ? (
                <>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="md:w-64" autoFocus />
                  <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Descrição" />
                  <Button size="icon" variant="ghost" onClick={() => saveEdit(c)} aria-label="Salvar"><Check size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditing(null)} aria-label="Cancelar"><X size={16} /></Button>
                </>
              ) : (
                <>
                  <div className="flex flex-col">
                    <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="Subir"><ArrowUp size={14} /></Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} aria-label="Descer"><ArrowDown size={14} /></Button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">/{c.slug}{c.description ? ` · ${c.description}` : ""}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full border ${n.live ? "bg-primary/10 text-primary border-primary/30" : "bg-muted text-muted-foreground border-transparent"}`}>
                    {n.live ? "Visível no blog" : "Sem artigos publicados"}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">{n.total} artigo(s)</span>
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(c.id); setEditName(c.name); setEditDesc(c.description ?? ""); }} aria-label="Editar"><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(c)} aria-label="Excluir"><Trash2 size={15} className="text-destructive" /></Button>
                </>
              )}
            </div>
          );
        })}
      </div>
      {dialog}
    </div>
  );
};

export default CategoriesManager;
